import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import ButtonBase from '@material-ui/core/ButtonBase'
import IconButton from '@material-ui/core/IconButton'
import Popover from '@material-ui/core/Popover'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import ArrowDropDownIcon from 'components/material-symbol/icons/ArrowDropDown'
import ChevronLeftIcon from 'components/material-symbol/icons/ChevronLeft'
import ChevronRightIcon from 'components/material-symbol/icons/ChevronRight'
import EventIcon from 'components/material-symbol/icons/Event'
import {
  CALLOUT_FOREGROUND,
  OUTLINED_INPUT_BORDER,
} from 'lib/form-field-styles'

const PRIMARY_BLUE = '#0053FF'
const RANGE_BAND = '#E0EAFF'
const MUTED_TEXT = '#6B7C87'
const FOOTER_TEXT = '#4A5A64'
const OUTSIDE_MONTH_TEXT = '#9AA5AD'
const BORDER = '#E0E0E0'
const CELL_SIZE = 40
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const CALENDAR_CELLS = 42

const useStyles = makeStyles(theme => ({
  // The coach filter trigger: the outlined 40px / 8px-radius button the
  // students, leads, academy students, accounts and sponsorships lists use.
  trigger: {
    height: 40,
    maxWidth: '100%',
    borderRadius: 8,
    fontWeight: 500,
    flexShrink: 0,
    borderColor: OUTLINED_INPUT_BORDER,
    color: CALLOUT_FOREGROUND,
  },
  triggerLabel: {
    whiteSpace: 'nowrap',
  },
  triggerIcon: {
    color: MUTED_TEXT,
  },
  triggerActive: {
    borderColor: PRIMARY_BLUE,
    backgroundColor: RANGE_BAND,
    color: '#001C57',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#D4E2FF',
      borderColor: PRIMARY_BLUE,
    },
  },
  triggerIconActive: {
    color: '#001C57',
  },
  paper: {
    marginTop: theme.spacing(0.5),
    maxWidth: 'calc(100% - 16px)',
    borderRadius: 16,
  },
  body: {
    display: 'flex',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  presets: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    flexShrink: 0,
    minWidth: 150,
    padding: theme.spacing(2.5, 2, 2, 3),
    borderRight: `1px solid ${BORDER}`,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing(0.5, 2),
      padding: theme.spacing(2, 2, 1),
      borderRight: 0,
      borderBottom: `1px solid ${BORDER}`,
    },
  },
  presetLink: {
    padding: theme.spacing(0.5, 0),
    fontSize: '0.9375rem',
    lineHeight: 1.5,
    color: PRIMARY_BLUE,
    '&:hover': {
      textDecoration: 'underline',
    },
    '&.Mui-focusVisible': {
      outline: `2px solid ${PRIMARY_BLUE}`,
      outlineOffset: 2,
    },
  },
  presetLinkActive: {
    fontWeight: 700,
  },
  calendars: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  calendar: {
    width: CELL_SIZE * 7,
  },
  calendarHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 44,
    marginBottom: theme.spacing(1),
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    color: CALLOUT_FOREGROUND,
  },
  calendarTitle: {
    display: 'flex',
    flex: 1,
    justifyContent: 'space-around',
    fontSize: '0.9375rem',
    fontWeight: 500,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  navButton: {
    padding: theme.spacing(0.75),
    color: CALLOUT_FOREGROUND,
    '&.Mui-disabled': {
      color: 'rgba(0, 0, 0, 0.26)',
    },
  },
  weekdays: {
    display: 'grid',
    gridTemplateColumns: `repeat(7, ${CELL_SIZE}px)`,
    borderBottom: `1px solid ${BORDER}`,
  },
  weekday: {
    height: 32,
    lineHeight: '32px',
    textAlign: 'center',
    fontSize: '0.8125rem',
    color: CALLOUT_FOREGROUND,
  },
  days: {
    display: 'grid',
    gridTemplateColumns: `repeat(7, ${CELL_SIZE}px)`,
    gridAutoRows: CELL_SIZE,
    paddingTop: theme.spacing(0.5),
  },
  cell: {
    position: 'relative',
    width: CELL_SIZE,
    height: CELL_SIZE,
  },
  cellInRange: {
    backgroundColor: RANGE_BAND,
  },
  cellRangeStart: {
    background: `linear-gradient(to right, transparent 50%, ${RANGE_BAND} 50%)`,
  },
  cellRangeEnd: {
    background: `linear-gradient(to left, transparent 50%, ${RANGE_BAND} 50%)`,
  },
  outsideDay: {
    display: 'block',
    width: CELL_SIZE,
    height: CELL_SIZE,
    lineHeight: `${CELL_SIZE}px`,
    textAlign: 'center',
    fontSize: '0.875rem',
    color: OUTSIDE_MONTH_TEXT,
  },
  day: {
    width: CELL_SIZE,
    height: CELL_SIZE,
    borderRadius: '50%',
    fontSize: '0.875rem',
    color: CALLOUT_FOREGROUND,
    '&:hover:not(.Mui-disabled)': {
      backgroundColor: 'rgba(0, 83, 255, 0.08)',
    },
    '&.Mui-disabled': {
      color: 'rgba(28, 27, 31, 0.38)',
      cursor: 'default',
    },
    '&.Mui-focusVisible': {
      outline: `2px solid ${PRIMARY_BLUE}`,
      outlineOffset: -2,
    },
  },
  dayToday: {
    color: PRIMARY_BLUE,
    fontWeight: 600,
  },
  daySelected: {
    backgroundColor: PRIMARY_BLUE,
    color: '#FFFFFF',
    fontWeight: 500,
    '&:hover:not(.Mui-disabled)': {
      backgroundColor: PRIMARY_BLUE,
    },
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    borderTop: `1px solid ${BORDER}`,
  },
  footerSpan: {
    flex: 1,
    minWidth: 0,
    paddingRight: theme.spacing(1),
    fontSize: '0.8125rem',
    color: FOOTER_TEXT,
  },
  footerButton: {
    minWidth: 64,
    color: PRIMARY_BLUE,
    fontWeight: 600,
  },
}))

const isDay = (a, b) => Boolean(a && b && a.hasSame(b, 'day'))

const getMonthCells = month => {
  const first = month.startOf('month')
  const lead = first.weekday % 7

  return Array.from({ length: CALENDAR_CELLS }, (_, index) =>
    first.plus({ days: index - lead }),
  )
}

// The span a picked range applies: the first day's month through the last day's
// month, e.g. "Aug 1 – Sep 30, 2026".
const formatWholeMonthSpan = ({ start, end }) => {
  const first = start.startOf('month')
  const last = end.endOf('month')
  const firstFormat = first.hasSame(last, 'year') ? 'LLL d' : 'LLL d, yyyy'

  return `${first.toFormat(firstFormat)} – ${last.toFormat('LLL d, yyyy')}`
}

// Opens on the end of a long range so the latest days are in view, and on the
// start of a short one, never past the last month the pickers allow.
const getInitialViewMonth = ({ start, end }, minDate, maxDate) => {
  const spanInMonths =
    (end.year - start.year) * 12 + end.month - start.month + 1
  const anchor = spanInMonths <= 2 ? start : end.minus({ months: 1 })
  const latest = maxDate.startOf('month').minus({ months: 1 })
  const earliest = minDate.startOf('month')
  const view = anchor.startOf('month')

  if (view > latest) return latest
  if (view < earliest) return earliest

  return view
}

const DayCell = ({
  day,
  month,
  pending,
  hoverDay,
  isDisabled,
  onPick,
  onHover,
  classes,
}) => {
  if (!day.hasSame(month, 'month')) {
    return <span className={classes.outsideDay}>{day.day}</span>
  }

  const { start, end } = pending
  const previewEnd = end || (start && hoverDay && hoverDay > start && hoverDay)
  const isStart = isDay(day, start)
  const isEnd = isDay(day, end)
  // Once both days are picked the band covers the whole months, since that is
  // what gets applied; while picking it follows the hovered day.
  const bandStart = start && end ? start.startOf('month') : start
  const bandEnd = start && end ? end.endOf('month').startOf('day') : previewEnd
  const hasBand = Boolean(bandStart && bandEnd && !isDay(bandStart, bandEnd))
  const isBandStart = hasBand && isDay(day, bandStart)
  const isBandEnd = hasBand && isDay(day, bandEnd)
  const isInside =
    hasBand && !isBandStart && !isBandEnd && day > bandStart && day < bandEnd
  const cellClass = [
    classes.cell,
    isInside && classes.cellInRange,
    isBandStart && classes.cellRangeStart,
    isBandEnd && classes.cellRangeEnd,
  ]
    .filter(Boolean)
    .join(' ')
  const isSelected = isStart || isEnd
  const dayClass = [
    classes.day,
    isSelected && classes.daySelected,
    !isSelected && isDay(day, DateTime.local()) && classes.dayToday,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Box className={cellClass}>
      <ButtonBase
        className={dayClass}
        disabled={isDisabled(day)}
        aria-label={day.toFormat('EEEE, MMMM d, yyyy')}
        aria-pressed={isSelected}
        onClick={() => onPick(day)}
        onMouseEnter={() => onHover(day)}
        onFocus={() => onHover(day)}
      >
        {day.day}
      </ButtonBase>
    </Box>
  )
}

const MonthCalendar = ({
  month,
  canGoBack,
  canGoForward,
  onShift,
  classes,
  ...dayProps
}) => (
  <Box
    className={classes.calendar}
    role="group"
    aria-label={month.toFormat('LLLL yyyy')}
  >
    <Box className={classes.calendarHeader}>
      <IconButton
        className={classes.navButton}
        aria-label="Previous month"
        disabled={!canGoBack}
        onClick={() => onShift(-1)}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <Typography component="span" className={classes.calendarTitle}>
        <span>{month.toFormat('LLL')}</span>
        <span>{month.toFormat('yyyy')}</span>
      </Typography>
      <IconButton
        className={classes.navButton}
        aria-label="Next month"
        disabled={!canGoForward}
        onClick={() => onShift(1)}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
    <Box className={classes.weekdays} aria-hidden="true">
      {WEEKDAYS.map(weekday => (
        <span key={weekday} className={classes.weekday}>
          {weekday}
        </span>
      ))}
    </Box>
    <Box className={classes.days}>
      {getMonthCells(month).map(day => (
        <DayCell
          key={day.toISODate()}
          day={day}
          month={month}
          classes={classes}
          {...dayProps}
        />
      ))}
    </Box>
  </Box>
)

// A range trigger button with a popover of two month calendars, preset links, and
// Cancel / OK. Days are picked one at a time (start, then end); OK applies and
// Cancel discards. `onApply({ start, end, presetId })` gets the picked days, and
// `presetId` is set when the range was chosen with a preset link.
const DateRangePicker = ({
  label,
  displayValue,
  isActive,
  value,
  presets,
  selectedPresetId,
  minDate,
  maxDate,
  maxMonths,
  onApply,
}) => {
  const classes = useStyles()
  const triggerRef = useRef(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [pending, setPending] = useState({
    start: null,
    end: null,
    presetId: null,
  })
  const [viewMonth, setViewMonth] = useState(null)
  const [hoverDay, setHoverDay] = useState(null)
  const isOpen = Boolean(anchorEl)
  const triggerIconClass = isActive
    ? classes.triggerIconActive
    : classes.triggerIcon
  const earliestMonth = minDate.startOf('month')
  const latestMonth = maxDate.startOf('month')

  const open = () => {
    setPending({ ...value, presetId: selectedPresetId })
    setViewMonth(getInitialViewMonth(value, minDate, maxDate))
    setHoverDay(null)
    setAnchorEl(triggerRef.current)
  }

  const close = () => setAnchorEl(null)

  const isDisabled = day => {
    if (day < minDate.startOf('day') || day > maxDate.endOf('day')) return true
    if (pending.start && !pending.end) {
      return day > pending.start.plus({ months: maxMonths - 1 }).endOf('month')
    }

    return false
  }

  const pick = day => {
    if (!pending.start || pending.end || day < pending.start) {
      setPending({ start: day, end: null, presetId: null })
    } else {
      setPending({ start: pending.start, end: day, presetId: null })
    }
  }

  const choosePreset = preset => {
    setPending({ start: preset.start, end: preset.end, presetId: preset.id })
    setViewMonth(getInitialViewMonth(preset, minDate, maxDate))
  }

  const apply = () => {
    onApply(pending)
    close()
  }

  const shiftMonths = delta => setViewMonth(viewMonth.plus({ months: delta }))

  return (
    <>
      <Button
        ref={triggerRef}
        variant="outlined"
        color="default"
        className={`${classes.trigger} ${
          isActive ? classes.triggerActive : ''
        }`}
        classes={{
          label: classes.triggerLabel,
          startIcon: triggerIconClass,
          endIcon: triggerIconClass,
        }}
        startIcon={<EventIcon fontSize="small" />}
        endIcon={<ArrowDropDownIcon />}
        aria-label={`${label}: ${displayValue}`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={open}
      >
        {displayValue}
      </Button>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          className: classes.paper,
          role: 'dialog',
          'aria-label': label,
        }}
      >
        <Box className={classes.body}>
          <Box className={classes.presets}>
            {presets.map(preset => (
              <ButtonBase
                key={preset.id}
                className={`${classes.presetLink} ${
                  pending.presetId === preset.id ? classes.presetLinkActive : ''
                }`}
                aria-pressed={pending.presetId === preset.id}
                onClick={() => choosePreset(preset)}
              >
                {preset.label}
              </ButtonBase>
            ))}
          </Box>
          <Box
            className={classes.calendars}
            onMouseLeave={() => setHoverDay(null)}
          >
            {viewMonth
              ? [viewMonth, viewMonth.plus({ months: 1 })].map(month => (
                <MonthCalendar
                  key={month.toISODate()}
                  month={month}
                  canGoBack={viewMonth > earliestMonth}
                  canGoForward={viewMonth.plus({ months: 1 }) < latestMonth}
                  onShift={shiftMonths}
                  classes={classes}
                  pending={pending}
                  hoverDay={hoverDay}
                  isDisabled={isDisabled}
                  onPick={pick}
                  onHover={setHoverDay}
                />
              ))
              : null}
          </Box>
        </Box>
        <Box className={classes.footer}>
          <Typography
            component="span"
            className={classes.footerSpan}
            aria-live="polite"
          >
            {pending.start && pending.end
              ? `Showing ${formatWholeMonthSpan(pending)}`
              : ''}
          </Typography>
          <Button className={classes.footerButton} onClick={close}>
            Cancel
          </Button>
          <Button
            className={classes.footerButton}
            disabled={!pending.start || !pending.end}
            onClick={apply}
          >
            OK
          </Button>
        </Box>
      </Popover>
    </>
  )
}

DateRangePicker.propTypes = {
  label: PropTypes.string.isRequired,
  displayValue: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  value: PropTypes.shape({
    start: PropTypes.object.isRequired,
    end: PropTypes.object.isRequired,
  }).isRequired,
  presets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      start: PropTypes.object.isRequired,
      end: PropTypes.object.isRequired,
    }),
  ).isRequired,
  selectedPresetId: PropTypes.string,
  minDate: PropTypes.object.isRequired,
  maxDate: PropTypes.object.isRequired,
  maxMonths: PropTypes.number.isRequired,
  onApply: PropTypes.func.isRequired,
}

export default DateRangePicker
